import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMailTemplates, updateMailTemplate, setSelectedTemplate } from '../store/slices/mailTemplatesSlice';
import { Box, Grid, List, ListItem, ListItemButton, ListItemText, Paper, TextField, Button, Typography, CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { getReadableTemplateName } from '../utils/mailTemplateHelper';

const quillStyles = {
    editor: {
        '& .ql-toolbar': {
            border: '1px solid #e0e0e0',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
            background: '#f8f9fa',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        },
        '& .ql-container': {
            border: '1px solid #e0e0e0',
            borderBottomLeftRadius: '8px',
            borderBottomRightRadius: '8px',
            borderTop: 'none',
            background: '#ffffff',
            minHeight: '300px',
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            fontSize: '16px',
        },
        '& .ql-editor': {
            minHeight: '300px',
            padding: '20px',
            lineHeight: '1.6',
        },
        '& .ql-toolbar button': {
            padding: '6px',
            margin: '2px',
            borderRadius: '4px',
            '&:hover': {
                background: '#e9ecef',
            },
        },
        '& .ql-formats': {
            marginRight: '12px',
        },
    },
};

export default function MailTemplates() {
    const dispatch = useDispatch();
    const { templates, selectedTemplate, loading, error } = useSelector(state => state.mailTemplates);
    const [editedSubject, setEditedSubject] = useState('');
    const [editedBody, setEditedBody] = useState('');

    const modules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'indent': '-1'}, { 'indent': '+1' }],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'align': [] }],
            ['link'],
            ['clean']
        ],
    };

    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'list', 'bullet', 'indent',
        'color', 'background',
        'align',
        'link'
    ];

    useEffect(() => {
        dispatch(fetchMailTemplates());
    }, [dispatch]);

    useEffect(() => {
        if (selectedTemplate) {
            setEditedSubject(selectedTemplate.subject);
            // DB'den gelen içeriği HTML formatına çevir
            let formattedBody = selectedTemplate.body;
            
            // \n karakterlerini <br> etiketine çevir
            formattedBody = formattedBody.replace(/\\n/g, '<br>');
            
            // \t karakterlerini tab boşluğuna çevir
            formattedBody = formattedBody.replace(/\\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;');
            
            // Tek satırlık \n karakterlerini de <br> yap
            formattedBody = formattedBody.replace(/\n/g, '<br>');
            
            setEditedBody(formattedBody);
        }
    }, [selectedTemplate]);

    useEffect(() => {
        if (error) {
            toast.error(error);
        }
    }, [error]);

    const handleTemplateSelect = (template) => {
        dispatch(setSelectedTemplate(template));
    };

    const handleSave = async () => {
        if (!selectedTemplate) return;

        try {
            // HTML içeriğini düzgün bir şekilde formatla
            let formattedBody = editedBody;
            
            // <br> etiketlerini \n karakterine çevir
            formattedBody = formattedBody.replace(/<br\s*\/?>/gi, '\n');
            
            // &nbsp; karakterlerini normal boşluğa çevir
            formattedBody = formattedBody.replace(/&nbsp;/g, ' ');
            
            // Fazla boşlukları temizle
            formattedBody = formattedBody.replace(/\n\s*\n/g, '\n');
            
            // Başındaki ve sonundaki boşlukları temizle
            formattedBody = formattedBody.trim();

            await dispatch(updateMailTemplate({
                mailTemplateId: selectedTemplate.mailTemplateId,
                body: formattedBody
            })).unwrap();
            toast.success('Mail şablonu başarıyla güncellendi');
        } catch (error) {
            toast.error('Mail şablonu güncellenirken bir hata oluştu');
        }
    };

    if (loading && !templates.length) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box p={3}>
            <Typography variant="h4" gutterBottom>
                Mail Şablonları
            </Typography>
            <Grid container spacing={3}>
                {/* Left side - Template List */}
                <Grid item xs={3}>
                    <Paper 
                        elevation={3}
                        sx={{
                            borderRadius: '12px',
                            overflow: 'hidden'
                        }}
                    >
                        <List component="nav">
                            {templates.map((template) => (
                                <ListItem key={template.mailTemplateId} disablePadding>
                                    <ListItemButton
                                        selected={selectedTemplate?.mailTemplateId === template.mailTemplateId}
                                        onClick={() => handleTemplateSelect(template)}
                                        sx={{
                                            '&.Mui-selected': {
                                                backgroundColor: '#f0f7ff',
                                                '&:hover': {
                                                    backgroundColor: '#e3f2fd',
                                                },
                                            },
                                        }}
                                    >
                                        <ListItemText 
                                            primary={getReadableTemplateName(template.name)}
                                            primaryTypographyProps={{
                                                style: {
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word'
                                                }
                                            }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </Grid>

                {/* Right side - Template Editor */}
                <Grid item xs={9}>
                    {selectedTemplate ? (
                        <Paper 
                            elevation={3} 
                            sx={{ 
                                p: 3,
                                borderRadius: '12px'
                            }}
                        >
                            <Box component="form" noValidate autoComplete="off">
                                <TextField
                                    label="Şablon Adı"
                                    value={getReadableTemplateName(selectedTemplate.name)}
                                    fullWidth
                                    disabled
                                    margin="normal"
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            borderRadius: '8px',
                                        }
                                    }}
                                />
                                <TextField
                                    label="Konu"
                                    value={editedSubject}
                                    onChange={(e) => setEditedSubject(e.target.value)}
                                    fullWidth
                                    margin="normal"
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            borderRadius: '8px',
                                        }
                                    }}
                                />
                                <Box mt={2} mb={2}>
                                    <Typography variant="subtitle1" gutterBottom>
                                        İçerik
                                    </Typography>
                                    <Box sx={quillStyles.editor}>
                                        <ReactQuill
                                            theme="snow"
                                            value={editedBody}
                                            onChange={setEditedBody}
                                            modules={modules}
                                            formats={formats}
                                        />
                                    </Box>
                                </Box>
                                <Box mt={6}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleSave}
                                        disabled={loading}
                                        sx={{
                                            borderRadius: '8px',
                                            textTransform: 'none',
                                            padding: '10px 24px',
                                            fontSize: '16px'
                                        }}
                                    >
                                        {loading ? <CircularProgress size={24} /> : 'Değişiklikleri Kaydet'}
                                    </Button>
                                </Box>
                            </Box>
                        </Paper>
                    ) : (
                        <Paper 
                            elevation={3} 
                            sx={{ 
                                p: 3, 
                                textAlign: 'center',
                                borderRadius: '12px',
                                minHeight: '200px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <Typography variant="h6" color="textSecondary">
                                Düzenlemek için listeden bir şablon seçin
                            </Typography>
                        </Paper>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
}

